import React, { useCallback, useContext, useMemo } from 'react';

import { ErrorBoundary } from 'react-error-boundary';

import Avatar from '@atlaskit/avatar';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import {
	TeamsAppAnalyticsContext,
	useAnalyticsEvents,
} from '@atlaskit/teams-app-internal-analytics';
import { token } from '@atlaskit/tokens';

import { firedAt } from '../analytics';
import {
	useFireContentRendered,
	useFireRequestFailed,
	useFireRequestSucceeded,
} from '../hooks/useProfileCardAnalytics';
import { CardOpenTimerProvider } from '../store/cardOpenTimer';
import { defaultGetTestId, UserProfileCardContext } from '../store/context';
import type { PersonProfileData } from '../types';
import { makeGetTestId } from '../utils/testId';

import { CardError } from './components/CardError';
import { CardErrorState } from './components/CardErrorState';
import { CardLoading } from './components/CardLoading';
import { HeaderActions } from './components/HeaderActions';
import { ManagerSection } from './components/ManagerSection';
import { RecentWorkSection } from './components/RecentWorkSection';

/**
 * Props for {@link UserProfileCard} - the presentational profile card.
 */
export interface UserProfileCardProps {
	/** Profile data to render. When omitted, the card shows a loading skeleton. */
	profileData?: PersonProfileData;
	/** URLs to render as smart-link previews in the recent-work section. */
	recentWorkData?: string[];
	/** When `true`, renders the loading skeleton even if `profileData` is supplied. */
	isLoading?: boolean;
	/** Test id forwarded to the card root. */
	testId?: string;
	/** Site ID of the tenant the viewer is currently in. */
	cloudId: string;
	/**
	 * The error that caused profile data to be unavailable, if any. Classified into the
	 * `errorType` / `errorStatusCode` attributes on `operational.profilecard.failed.request`.
	 * When omitted and `profileData` is absent, the request is classified as `NotFound`.
	 */
	error?: unknown;
}

const styles = cssMap({
	root: {
		width: '360px',
		minWidth: '340px',
		cursor: 'default',
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
		backgroundColor: token('elevation.surface.raised'),
	},
});

/**
 * Presentational profile card displaying a user's core information and recent work.
 * Renders avatar, name, pronouns, job title, department, location, manager, and recent-work items.
 *
 * Shows a loading skeleton when `isLoading` is true or `profileData` is undefined.
 * Displays an error state when profile data is missing or `name` is not provided.
 *
 * Recent-work previews require a `SmartCardProvider` from `@atlaskit/link-provider`
 * in scope (already provided by every Atlassian product shell).
 */
export function UserProfileCard({
	profileData,
	recentWorkData,
	isLoading = false,
	testId = 'user-profile-card',
	cloudId,
	error,
}: UserProfileCardProps): React.JSX.Element {
	const outerContext = useContext(UserProfileCardContext);
	const userId = profileData?.accountId ?? '';
	const localGetTestId = useMemo(() => makeGetTestId(userId, testId), [userId, testId]);
	// Forward outer values so trigger-wrapped + standalone consumers see the same shape;
	// fall back to a local getTestId factory when there is no outer provider.
	const contextValue = useMemo(
		() => ({
			...outerContext,
			cloudId,
			getTestId:
				outerContext.getTestId !== defaultGetTestId ? outerContext.getTestId : localGetTestId,
		}),
		[cloudId, localGetTestId, outerContext],
	);

	const analyticsData = {
		source: 'userProfileCard' as const,
		attributes: {
			cloudId,
			userId: userId,
			hasManager: Boolean(profileData?.manager),
			hasRecentWork: Boolean(recentWorkData?.length),
			hasThirdPartyIntegrations: Boolean(profileData?.thirdPartyIntegrations?.length),
		},
	};

	return (
		<TeamsAppAnalyticsContext data={analyticsData}>
			<UserProfileCardContext.Provider value={contextValue}>
				<CardOpenTimerProvider>
					<UserProfileCardErrorBoundary testId={testId}>
						<UserProfileCardContent
							profileData={profileData}
							recentWorkData={recentWorkData}
							isLoading={isLoading}
							testId={testId}
							cloudId={cloudId}
							error={error}
						/>
					</UserProfileCardErrorBoundary>
				</CardOpenTimerProvider>
			</UserProfileCardContext.Provider>
		</TeamsAppAnalyticsContext>
	);
}

function UserProfileCardErrorBoundary({
	children,
	testId,
}: {
	children: React.ReactNode;
	testId: string;
}): React.JSX.Element {
	const { fireEvent } = useAnalyticsEvents();

	const handleError = useCallback(
		(_error: Error) => {
			// `default` (not `renderError`) - schema only allows `default | NotFound`.
			fireEvent('ui.profilecard.rendered.error', {
				firedAt: firedAt(),
				errorType: 'default',
				hasRetry: false,
			});
		},
		[fireEvent],
	);

	return (
		<ErrorBoundary onError={handleError} fallback={<CardError testId={testId} />}>
			{children}
		</ErrorBoundary>
	);
}

function UserProfileCardContent({
	profileData,
	recentWorkData,
	isLoading,
	testId,
	cloudId,
	error,
}: Required<Pick<UserProfileCardProps, 'isLoading' | 'testId' | 'cloudId'>> &
	Pick<UserProfileCardProps, 'profileData' | 'recentWorkData' | 'error'>): React.JSX.Element {
	const { getTestId } = useContext(UserProfileCardContext);

	const hasProfileData = Boolean(profileData && profileData.name);

	useFireRequestSucceeded(!isLoading && hasProfileData);
	useFireRequestFailed(!isLoading && !hasProfileData ? error ?? null : undefined);

	if (isLoading) {
		return <CardLoading testId={testId} />;
	}

	if (!profileData || !profileData.name) {
		return <CardErrorState testId={testId} errorType="NotFound" />;
	}

	return (
		<UserProfileCardRenderedContent
			profileData={profileData}
			recentWorkData={recentWorkData}
			testId={testId}
			cloudId={cloudId}
			getTestId={getTestId}
		/>
	);
}

// Success-only render. Split out so `useFireContentRendered` mounts (and fires)
// only when the user actually sees the content - not on loading or error.
function UserProfileCardRenderedContent({
	profileData,
	recentWorkData,
	testId,
	cloudId,
	getTestId,
}: {
	profileData: PersonProfileData;
	recentWorkData?: string[];
	testId: string;
	cloudId: string;
	getTestId: (suffix: string) => string | undefined;
}): React.JSX.Element {
	useFireContentRendered(profileData);

	const { name, pronouns, avatarUrl, jobTitle, department, location, localTime, manager } =
		profileData;

	const subtitleParts = [jobTitle, department].filter(Boolean);
	const subtitle = subtitleParts.join(` \u2022 `);

	const locationLine =
		localTime && location ? `${location} \u2013 ${localTime}` : location ?? undefined;

	const hasRecentWork = Boolean(recentWorkData?.length);

	return (
		<Box xcss={styles.root} testId={testId} role="region" aria-label={name}>
			{/* Legacy testId - matches `@atlaskit/profilecard` so existing consumer tests keep passing. */}
			<Stack space="space.150" testId="profilecard">
				<Stack space="space.075">
					<Inline spread="space-between" alignBlock="start">
						<Avatar
							src={avatarUrl ?? undefined}
							name={name}
							size="xlarge"
							testId={getTestId('avatar')}
						/>
						<HeaderActions profileData={profileData} cloudId={cloudId} />
					</Inline>

					<Stack space="space.050">
						<Inline space="space.100" alignBlock="end">
							<Heading as="h2" size="medium" testId={getTestId('name')}>
								{name}
							</Heading>
							{pronouns && (
								<Text size="small" color="color.text.subtlest" testId={getTestId('pronouns')}>
									{pronouns}
								</Text>
							)}
						</Inline>
						{subtitle && (
							<Text
								maxLines={2}
								size="small"
								color="color.text.subtlest"
								testId={getTestId('subtitle')}
							>
								{subtitle}
							</Text>
						)}
						{locationLine && (
							<Text
								maxLines={1}
								size="small"
								color="color.text.subtlest"
								testId={getTestId('location')}
							>
								{locationLine}
							</Text>
						)}
					</Stack>
				</Stack>

				{manager && <ManagerSection manager={manager} testId={getTestId('manager')} />}

				{hasRecentWork && recentWorkData && (
					<RecentWorkSection recentWorkData={recentWorkData} testId={getTestId('recent-work')} />
				)}
			</Stack>
		</Box>
	);
}
